import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { PagedResponse } from '@app/shared/api/models/page.model';
import { FilterOperatorEnum } from '@app/shared/tables/enums/filter-operator.enum';
import { FilterSortConfigModel } from '@app/shared/tables/types/filter-sort-config.model';
import { CurrentUserPermissionsDto } from '@core/permissions/models/permissions.models';
import { TgAccountModel } from '@entities/tg-account/models/tg-account.model';
import { UserGroupRoleEnum } from '@entities/user/enums/user-group-role.enum';
import { UserModel } from '@entities/user/models/user.model';
import { Observable } from 'rxjs';

const USERS_SEARCH_PAGE_SIZE = 1000;

@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  private readonly _httpClient: HttpClient = inject(HttpClient);

  private readonly _subscriptionStatusFilterKey = 'subscriptionStatus';

  public getCurrentUser(): Observable<UserModel> {
    return this._httpClient.get<UserModel>('users/me');
  }

  public getCurrentUserPermissions(): Observable<CurrentUserPermissionsDto> {
    return this._httpClient.get<CurrentUserPermissionsDto>('users/me/permissions');
  }

  public getPersonalizedTgBotLink(): Observable<string> {
    return this._httpClient.get('users/me/tg/link', { responseType: 'text' });
  }

  public getGroupUsers(
    groupId: number,
    role: UserGroupRoleEnum,
    { pageable, searchCriteriaList }: FilterSortConfigModel
  ): Observable<PagedResponse<UserModel>> {
    const statusFilter = searchCriteriaList.find(f => f.key === this._subscriptionStatusFilterKey);
    const bodyCriteria = searchCriteriaList.filter(f => f.key !== this._subscriptionStatusFilterKey);
    const params = {
      ...pageable,
      role,
      ...(statusFilter && { subscriptionStatus: statusFilter.value }),
    };

    return this._httpClient.post<PagedResponse<UserModel>>(`users/group/${groupId}`, bodyCriteria, { params });
  }

  public getAllUsersList({
    pageable,
    searchCriteriaList,
  }: FilterSortConfigModel): Observable<PagedResponse<UserModel>> {
    return this._httpClient.post<PagedResponse<UserModel>>('users/all', searchCriteriaList, {
      params: { ...pageable },
    });
  }

  public getAllUsers(
    search: string = '',
    groupId: number,
    role: UserGroupRoleEnum
  ): Observable<PagedResponse<UserModel>> {
    const filters = [
      {
        key: 'fullNameEmail',
        operator: FilterOperatorEnum.Contains,
        value: search,
      },
      {
        key: 'groupId',
        operator: FilterOperatorEnum.Equal,
        value: groupId,
      },
      {
        key: 'groupRole',
        operator: FilterOperatorEnum.Equal,
        value: role,
      },
    ];

    return this._httpClient.post<PagedResponse<UserModel>>('users/all', filters, {
      params: { size: USERS_SEARCH_PAGE_SIZE },
    });
  }

  public changeUserTgId(userId: number, telegramId: number): Observable<void> {
    return this._httpClient.patch<void>(`users/${userId}/telegram-id/${telegramId}`, {});
  }

  public getCurrentTgAccount(): Observable<TgAccountModel> {
    return this._httpClient.get<TgAccountModel>('users/tg-admin');
  }
}
